import { assertNever } from "@impero-com/common/utils/obj";
import uuid from "uuid";

import {
    RiskDirectoryResource,
    Section,
    UpdatedRiskDirectoryStructure,
} from "api/generated/model";
import { FieldType, MetadataField, OptionValue } from "org/risks/types";

import { NumberOfInfinity } from "./components/rangeMin";
import { RDStructureField } from "./fields";
import { RDStructureFieldType } from "./fields/fieldType";
import { RDStructureOption } from "./options";
import { FormSchema } from "./schema";
import { RDStructureSection, SectionLevel } from "./sections";
import { RDStructureSubsection } from "./subsections";

export const newDefaultOption = (): RDStructureOption => {
    return {
        optionId: uuid.v4(),
        value: "Value",
        weight: 0,
        rangeMin: { type: "NegInfinity" },
    };
};

export function newFieldType(
    fieldType: RDStructureFieldType["type"]
): RDStructureFieldType {
    switch (fieldType) {
        case "Heading": {
            return {
                type: fieldType,
            };
        }
        case "TextArea": {
            return {
                type: fieldType,
            };
        }
        case "TextInput": {
            return {
                type: fieldType,
            };
        }
        case "AttachedFiles": {
            return {
                type: fieldType,
            };
        }
        case "NumericInput": {
            return {
                type: fieldType,
                formula: "",
            };
        }
        case "CheckboxGroup": {
            return {
                type: fieldType,
                options: [newDefaultOption()],
            };
        }
        case "Dropdown": {
            return {
                type: fieldType,
                options: [newDefaultOption()],
                formula: "",
            };
        }
        case "RadioButtonGroup": {
            return {
                type: fieldType,
                options: [newDefaultOption()],
                formula: "",
            };
        }
        default: {
            return assertNever(fieldType);
        }
    }
}

export function newField(option: FieldSelectOption): RDStructureField {
    return {
        fieldId: uuid.v4(),
        requiredForAssessment: false,
        name: option.label,
        condition: null,
        fieldType: newFieldType(option.value as RDStructureFieldType["type"]),
    };
}

export interface FieldSelectOption {
    label: string;
    value: string;
}

export const fieldSelectOptions: FieldSelectOption[] = [
    { label: "Heading", value: "Heading" },
    { label: "Text Area", value: "TextArea" },
    { label: "Text Input", value: "TextInput" },
    { label: "Attached Files", value: "AttachedFiles" },
    { label: "Numeric Input", value: "NumericInput" },
    { label: "Checkbox", value: "CheckboxGroup" },
    { label: "Dropdown", value: "Dropdown" },
    { label: "Radio Button", value: "RadioButtonGroup" },
];

export const newDefaultField = (): RDStructureField => {
    return {
        fieldId: uuid.v4(),
        name: "Title",
        requiredForAssessment: false,
        condition: null,
        fieldType: {
            type: "Heading",
        },
    };
};

export const newDefaultSubsection = (): RDStructureSubsection => {
    return {
        fields: [newDefaultField()],
    };
};

export const newDefaultSection = (
    sectionLevel: SectionLevel
): RDStructureSection => {
    return {
        sectionId: uuid.v4(),
        subsections: [newDefaultSubsection()],
        level: sectionLevel,
    };
};

export type FieldWithLevel =
    | { level: "RiskDirectory"; field: MetadataField }
    | { level: "Entity"; field: MetadataField };

export function buildFormValuesFromApi(
    resource: RiskDirectoryResource
): FormSchema {
    const { structure } = resource;
    const getFieldById = (id: string): FieldWithLevel => {
        const maybeRdLevelField = structure.nodeTypeToDirectoryLevelFields[
            "Risk"
        ]?.find((f) => f.id === id);
        if (maybeRdLevelField !== undefined) {
            return { level: "RiskDirectory", field: maybeRdLevelField };
        }
        const maybeEntityLevelField = structure.entityLevelFields.find(
            (f) => f.id === id
        );
        if (maybeEntityLevelField !== undefined) {
            return { level: "Entity", field: maybeEntityLevelField };
        }
        throw new Error("Field not found for ID " + id);
    };
    const sections: Array<RDStructureSection> = structure.sections.map(
        (section) => {
            const subsections: Array<RDStructureSubsection> =
                section.sectionColumns.map((subsectionFieldIds) => {
                    const fields: Array<RDStructureField> =
                        subsectionFieldIds.map((fieldId) => {
                            const field = getFieldById(fieldId).field;
                            return {
                                fieldId: field.id,
                                name: field.name,
                                condition: field.condition ?? null,
                                fieldType: apiFieldTypeToFormFieldType(
                                    field.fieldType
                                ),
                                requiredForAssessment:
                                    field.requiredForAssessment,
                            };
                        });
                    return {
                        fields,
                    };
                });

            // TODO: Redo this. Can be cleaner.
            const fieldLevels = subsections
                .flatMap((subsections) => subsections.fields)
                .map((field) => getFieldById(field.fieldId).level);
            let level: SectionLevel = "Entity";
            if (fieldLevels.every((f) => f === "Entity")) {
                level = "Entity";
            }
            if (fieldLevels.every((f) => f === "RiskDirectory")) {
                level = "RiskDirectory";
            }
            // --- END TODO

            return {
                sectionId: section.id,
                level,
                subsections,
            };
        }
    );
    return { sections };
}

function apiOptionValueToFormOptionValue(
    optionValue: OptionValue
): RDStructureOption {
    let rangeMin: NumberOfInfinity = { type: "Number", value: 0 };
    switch (optionValue.rangeMin.type) {
        case "NegInfinity":
            rangeMin = { type: "NegInfinity" };
            break;
        case "Number":
            rangeMin = { type: "Number", value: optionValue.rangeMin.value };
            break;
        case "Infinity":
            console.warn(
                "Impossible case +Infinity received from API, will default to 0"
            );
            break;
    }
    return {
        optionId: optionValue.id,
        value: optionValue.value,
        weight: optionValue.weight,
        rangeMin,
    };
}

function apiFieldTypeToFormFieldType(
    fieldType: FieldType
): RDStructureFieldType {
    switch (fieldType.type) {
        case "Heading":
        case "TextArea":
        case "TextInput":
        case "AttachedFiles":
            return fieldType;
        case "NumericInput":
            return {
                type: fieldType.type,
                formula: fieldType.formula ?? "",
            };
        case "Dropdown":
        case "RadioButtonGroup":
            return {
                type: fieldType.type,
                options: fieldType.options.map(apiOptionValueToFormOptionValue),
                formula: fieldType.formula ?? "",
            };
        case "CheckboxGroup":
            return {
                type: fieldType.type,
                options: fieldType.options.map(apiOptionValueToFormOptionValue),
            };
        default:
            return assertNever(fieldType);
    }
}

function formOptionValueToApiOptionValue(
    optionValue: RDStructureOption
): OptionValue {
    return {
        id: optionValue.optionId,
        weight: optionValue.weight,
        value: optionValue.value,
        rangeMin: optionValue.rangeMin,
    };
}
function formFieldTypeToApiFieldType(
    fieldType: RDStructureFieldType
): FieldType {
    switch (fieldType.type) {
        case "Heading":
        case "TextArea":
        case "TextInput":
        case "AttachedFiles":
            return fieldType;
        case "NumericInput":
            return {
                type: fieldType.type,
                formula:
                    fieldType.formula !== "" ? fieldType.formula : undefined,
            };
        case "Dropdown":
        case "RadioButtonGroup":
            return {
                type: fieldType.type,
                options: fieldType.options.map(formOptionValueToApiOptionValue),
                formula:
                    fieldType.formula !== "" ? fieldType.formula : undefined,
            };
        case "CheckboxGroup":
            return {
                type: fieldType.type,
                options: fieldType.options.map(formOptionValueToApiOptionValue),
            };
        default:
            return assertNever(fieldType);
    }
}

export function buildApiPayloadFromFormValues(
    values: FormSchema
): UpdatedRiskDirectoryStructure {
    let entityLevelFields: Array<MetadataField> = [];
    let riskLevelFields: Array<MetadataField> = [];
    for (const section of values.sections) {
        const fields: Array<MetadataField> = section.subsections
            .flatMap((sub) => sub.fields)
            .map((field) => {
                return {
                    id: field.fieldId,
                    name: field.name,
                    fieldType: formFieldTypeToApiFieldType(field.fieldType),
                    condition: field.condition,
                    requiredForAssessment: field.requiredForAssessment,
                };
            });
        switch (section.level) {
            case "RiskDirectory":
                riskLevelFields = [...riskLevelFields, ...fields];
                break;
            case "Entity":
                entityLevelFields = [...entityLevelFields, ...fields];
                break;
            default:
                assertNever(section.level);
        }
    }
    const sectionsForPayload: Array<Section> = values.sections.map(
        (section) => {
            const sectionColumns = section.subsections.map((subsection) => {
                return subsection.fields.map((field) => field.fieldId);
            });
            return {
                id: section.sectionId,
                sectionColumns,
            };
        }
    );

    return {
        sections: sectionsForPayload,
        entityLevelFields,
        nodeTypeToDirectoryLevelFields: {
            // We do not support process-level fields for now
            Process: [],
            Risk: riskLevelFields,
        },
    };
}
